import { MetadataFieldType } from '@/api/generatedApi';
import { useUpdateAsset } from '@/hooks/api/assets/useUpdateAsset';
import { useCreateMetadataFields } from '@/hooks/api/metadata/useCreateMetadataFields';
import { normalizeFilterValue } from '@/hooks/useSearchFilters';
import { AxiosApiError, getApiError } from '@/types/apiError';

export const useBulkMetadataUpdate = () => {
  const { updateAssetAsync } = useUpdateAsset();
  const { createMetadataField } = useCreateMetadataFields();

  const bulkUpdateAssetMetadataBatch = async (
    updates: Array<{
      assetId: string;
      metadata: Record<string, string>;
    }>,
    fieldDefinitions: { name: string; type: MetadataFieldType; isNew: boolean }[],
    onProgress?: (completed: number, total: number, currentAssetId: string) => void
  ) => {
    const newFields = fieldDefinitions.filter(field => field.isNew);

    for (const fieldDefinition of newFields) {
      try {
        await new Promise<void>((resolve, reject) => {
          createMetadataField(
            {
              name: fieldDefinition.name,
              type: fieldDefinition.type,
            },
            {
              onSuccess: () => resolve(),
              onError: (error: Error) => {
                const axiosError = error as unknown as AxiosApiError;
                if (axiosError?.response?.status === 409) {
                  resolve();
                } else {
                  reject(error);
                }
              },
            }
          );
        });
      } catch (error) {
        if ((error as AxiosApiError)?.response?.status === 409) {
          continue;
        }
        throw error;
      }
    }

    const allMetadataFields = fieldDefinitions.map(field => ({ name: field.name, type: field.type }));

    const normalizedUpdates = updates.map(update => {
      const normalizedMetadata: Record<string, string | number | boolean> = {};

      for (const [fieldName, value] of Object.entries(update.metadata)) {
        const normalizedValue = normalizeFilterValue({
          value,
          fieldName,
          metadataFields: allMetadataFields,
        });
        if (normalizedValue !== undefined) {
          normalizedMetadata[fieldName] = normalizedValue;
        }
      }

      return {
        assetId: update.assetId,
        metadata: normalizedMetadata,
      };
    });

    const results: Array<{ assetId: string; success: boolean; error?: string }> = [];

    for (let index = 0; index < normalizedUpdates.length; index++) {
      const update = normalizedUpdates[index];

      onProgress?.(index, normalizedUpdates.length, update.assetId);

      try {
        await updateAssetAsync(
          {
            assetId: update.assetId,
            data: { metadata: update.metadata },
          },
          {
            onSuccess: () => {
              results.push({ assetId: update.assetId, success: true });
            },
            onError: (error: Error) => {
              results.push({
                assetId: update.assetId,
                success: false,
                error: getApiError(error)?.message ?? error.message,
              });
            },
          }
        );
      } catch {
        continue;
      }
    }

    onProgress?.(normalizedUpdates.length, normalizedUpdates.length, '');

    return results;
  };

  return {
    bulkUpdateAssetMetadataBatch,
  };
};
